import React, { useEffect, useRef, useState } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, Divider, Switch, useToast, Icon, Link,
  IconButton, Wrap, WrapItem, Tag, TagLabel, TagCloseButton,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiPlus } from 'react-icons/fi';
import deportesApi, { Convocatoria, ArchivoGenerico } from '../../../api/deportesApi';
import {
  SectionTitle, Field, useCatalogos, fmtFecha, ROJO, ROJO_HOVER, API_UPLOADS,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: Convocatoria;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

export const ESTATUS_CONVOCATORIA = ['Borrador', 'Programada', 'Vigente', 'Cerrada', 'Cancelada'];

const FormularioConvocatoria: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const { disciplinas, categorias } = useCatalogos();
  const archivoRef = useRef<HTMLInputElement>(null);

  const [f, setF] = useState<any>({
    nombre: data?.nombre || '',
    descripcion: data?.descripcion || '',
    disciplinaId: data?.disciplinaId ? String(data.disciplinaId) : '',
    fechaPublicacion: data?.fechaPublicacion ? data.fechaPublicacion.split('T')[0] : (modo === 'new' ? new Date().toISOString().split('T')[0] : ''),
    fechaCierre: data?.fechaCierre ? data.fechaCierre.split('T')[0] : '',
    responsable: data?.responsable || '',
    requisitos: data?.requisitos || '',
    permiteRegistro: data?.permiteRegistro ?? false,
    estatus: data?.estatus || 'Borrador',
  });
  const [guardando, setGuardando] = useState(false);

  // categorías vinculadas y archivos (modo edit/view con id)
  const [cats, setCats] = useState<any[]>([]);
  const [archivos, setArchivos] = useState<ArchivoGenerico[]>([]);
  const [catSel, setCatSel] = useState('');
  const [subiendo, setSubiendo] = useState(false);

  const id = data?.id;

  const cargarSub = async () => {
    if (!id) return;
    const [c, a] = await Promise.all([
      deportesApi.listarConvocatoriaCategorias(id).catch(() => []),
      deportesApi.listarConvocatoriaArchivos(id).catch(() => []),
    ]);
    setCats(c || []);
    setArchivos(a || []);
  };
  useEffect(() => { if (modo !== 'new') cargarSub(); /* eslint-disable-next-line */ }, [id]);

  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));

  const handleGuardar = async () => {
    if (!f.nombre) { toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 3000 }); return; }
    setGuardando(true);
    try {
      const payload = { ...f, disciplinaId: f.disciplinaId ? Number(f.disciplinaId) : null };
      if (modo === 'edit' && data) await deportesApi.actualizarConvocatoria(data.id, payload);
      else await deportesApi.crearConvocatoria(payload);
      toast({ title: modo === 'edit' ? 'Convocatoria actualizada' : 'Convocatoria creada', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (e: any) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'No se pudo guardar.', status: 'error', duration: 4000 });
    } finally { setGuardando(false); }
  };

  const addCat = async () => {
    if (!id || !catSel) return;
    try { await deportesApi.agregarConvocatoriaCategoria(id, Number(catSel)); setCatSel(''); cargarSub(); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };
  const delCat = async (relId: number) => {
    try { await deportesApi.eliminarConvocatoriaCategoria(relId); cargarSub(); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };

  const subirArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      await deportesApi.subirConvocatoriaArchivo(id, fd);
      toast({ title: 'Archivo subido', status: 'success', duration: 2000 });
      cargarSub();
    } catch (err: any) { toast({ title: 'Error', description: err?.response?.data?.message, status: 'error', duration: 3000 }); }
    finally { setSubiendo(false); if (archivoRef.current) archivoRef.current.value = ''; }
  };
  const delArchivo = async (aid: number) => {
    try { await deportesApi.eliminarConvocatoriaArchivo(aid); cargarSub(); }
    catch (e: any) { toast({ title: 'Error', description: e?.response?.data?.message, status: 'error', duration: 3000 }); }
  };

  const SubCategorias = () => (
    <>
      <SectionTitle>Categorías participantes</SectionTitle>
      {modo !== 'view' && (
        <HStack>
          <Select size="sm" placeholder="Selecciona categoría" value={catSel} onChange={(e) => setCatSel(e.target.value)} maxW="260px">
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="red" variant="outline" onClick={addCat} isDisabled={!catSel}>Agregar</Button>
        </HStack>
      )}
      <Wrap mt={2}>
        {cats.length === 0 && <Text fontSize="sm" color="gray.400">Sin categorías vinculadas</Text>}
        {cats.map((c: any) => (
          <WrapItem key={c.id}>
            <Tag colorScheme="red" borderRadius="full">
              <TagLabel>{c.categoria?.nombre || c.nombre || `Cat #${c.categoriaId}`}</TagLabel>
              {modo !== 'view' && <TagCloseButton onClick={() => delCat(c.id)} />}
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
    </>
  );

  const SubArchivos = () => (
    <>
      <SectionTitle>Archivos de la convocatoria</SectionTitle>
      {modo !== 'view' && (
        <>
          <input ref={archivoRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={subirArchivo} />
          <Button size="sm" leftIcon={<FiUpload />} colorScheme="red" variant="outline" onClick={() => archivoRef.current?.click()} isLoading={subiendo}>
            Subir archivo (bases / PDF / imagen)
          </Button>
        </>
      )}
      <VStack align="stretch" mt={2} spacing={1}>
        {archivos.length === 0 && <Text fontSize="sm" color="gray.400">Sin archivos</Text>}
        {archivos.map((a) => (
          <HStack key={a.id} justify="space-between">
            <HStack>
              <Icon as={FiFile} color="red.500" />
              <Link href={`${API_UPLOADS}${a.rutaArchivo}`} isExternal color="blue.500" fontSize="sm">{a.nombreArchivo}</Link>
            </HStack>
            {modo !== 'view' && <IconButton aria-label="Eliminar" icon={<Icon as={FiFile} />} size="xs" colorScheme="red" variant="ghost" onClick={() => delArchivo(a.id)} />}
          </HStack>
        ))}
      </VStack>
    </>
  );

  // ────────── MODO VISTA ──────────
  if (modo === 'view') {
    const d = data as Convocatoria;
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos de la convocatoria</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="FOLIO" value={d.folio} />
          <Field label="ESTATUS" value={<Badge colorScheme="cyan">{d.estatus}</Badge>} />
          <Box gridColumn={{ md: 'span 2' }}><Field label="NOMBRE" value={d.nombre} /></Box>
          <Field label="DISCIPLINA" value={d.disciplina?.nombre} />
          <Field label="RESPONSABLE" value={d.responsable} />
          <Field label="PUBLICACIÓN" value={fmtFecha(d.fechaPublicacion)} />
          <Field label="CIERRE" value={fmtFecha(d.fechaCierre)} />
          <Field label="PORTAL" value={<Badge colorScheme={d.publicadaPortal ? 'green' : 'gray'}>{d.publicadaPortal ? 'Publicada' : 'No publicada'}</Badge>} />
          <Field label="REGISTRO EN LÍNEA" value={d.permiteRegistro ? 'Sí' : 'No'} />
        </SimpleGrid>
        {d.descripcion && (<><SectionTitle>Descripción</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.descripcion}</Text></>)}
        {d.requisitos && (<><SectionTitle>Requisitos</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.requisitos}</Text></>)}
        <SubCategorias />
        <SubArchivos />
      </VStack>
    );
  }

  // ────────── NUEVO / EDITAR ──────────
  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de la convocatoria</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Nombre</FormLabel>
          <Input value={f.nombre} onChange={(e) => set('nombre', e.target.value)} placeholder="Ej. Convocatoria Selección Municipal de Atletismo" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Disciplina</FormLabel>
          <Select placeholder="Selecciona" value={f.disciplinaId} onChange={(e) => set('disciplinaId', e.target.value)}>
            {disciplinas.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus} onChange={(e) => set('estatus', e.target.value)}>
            {ESTATUS_CONVOCATORIA.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha de publicación</FormLabel>
          <Input type="date" value={f.fechaPublicacion} onChange={(e) => set('fechaPublicacion', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha de cierre</FormLabel>
          <Input type="date" value={f.fechaCierre} onChange={(e) => set('fechaCierre', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Responsable</FormLabel>
          <Input value={f.responsable} onChange={(e) => set('responsable', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Descripción</FormLabel>
          <Textarea rows={3} value={f.descripcion} onChange={(e) => set('descripcion', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Requisitos</FormLabel>
          <Textarea rows={3} value={f.requisitos} onChange={(e) => set('requisitos', e.target.value)} />
        </FormControl>
        <FormControl display="flex" alignItems="center" gap={3}>
          <Switch colorScheme="red" isChecked={f.permiteRegistro} onChange={(e) => set('permiteRegistro', e.target.checked)} />
          <FormLabel fontSize="sm" mb={0}>Permite registro en línea</FormLabel>
        </FormControl>
      </SimpleGrid>

      {modo === 'edit' && (<><SubCategorias /><SubArchivos /></>)}
      {modo === 'new' && (
        <Text fontSize="xs" color="gray.500">Guarda la convocatoria para agregar categorías y archivos.</Text>
      )}

      <Divider />
      <HStack justify="flex-end" pt={1} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">Guardar</Button>
      </HStack>
    </VStack>
  );
};

export default FormularioConvocatoria;
